import { Link } from "react-router-dom";
import SiteHeader from "../../components/feature/SiteHeader";
import SiteFooter from "../../components/feature/SiteFooter";
import PageHero from "../../components/feature/PageHero";
import usePageTitle from "../../hooks/usePageTitle";
import { endingCover } from "../../mocks/site";
import { articles } from "../../mocks/articles";

const timeline = [
  { no: "01", title: "守护蛋孵化，旅程开始", desc: "亚梦的三颗守护蛋孵化，守护者与复活社的世界观由此展开。" },
  { no: "02", title: "X 蛋之谜浮出水面", desc: "守护蛋的污染与净化，让『理解真心』成为贯穿全作的主题。" },
  { no: "03", title: "几斗身世与复活社阴影", desc: "主线冲突升级，家族的过去与对力量的执念交织在一起。" },
  { no: "04", title: "守护蛋的抉择与告别", desc: "当守护蛋完成使命，关于离开与成长的选择成为核心。" },
  { no: "05", title: "温暖收束，迎向未来", desc: "结局回归接纳与陪伴，守护者与甜心们共同走向新的明天。" },
];

const endings = [
  { name: "亚梦", role: "守护者 Joker", desc: "在一次次守护中真正接纳了自己，甜心们继续以伙伴身份陪伴她走向更远的路。" },
  { name: "几斗", role: "神秘的猫系少年", desc: "摆脱复活社的阴影，得以按自己的方式生活，与亚梦的羁绊始终温暖。" },
  { name: "守护甜心", role: "理想中的自己", desc: "完成使命后以全新的形式陪伴主人，象征成长的延续而非告别。" },
  { name: "复活社", role: "追逐力量的势力", desc: "对守护蛋的执念因对真心的理解而软化，留下开放而温和的落点。" },
];

export default function Ending() {
  usePageTitle("结局剧情详解");

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "守护甜心结局剧情详解：所有角色的最终归宿",
    description: "守护甜心系列结局逐层拆解，包含主线时间线、各角色最终归宿与开放结局背后的温柔主题。",
    inLanguage: "zh-CN",
    dateModified: "2026-08-15",
    author: { "@type": "Organization", name: "甜心剧情站" },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://sequelio.github.io/" },
        { "@type": "ListItem", position: 2, name: "结局详解", item: "https://sequelio.github.io/ending" },
      ],
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <SiteHeader />
      <main>
        <PageHero
          crumbs={[{ label: "首页", path: "/" }, { label: "结局详解" }]}
          title="结局剧情详解"
          subtitle="守护甜心的结局没有谁是彻底的输家，也没有仓促的收尾。这里为你逐层拆解主线时间线、每位角色的最终归宿，以及开放结局背后的温柔。"
          badge="结局解析 · 角色归宿"
        />

        <section className="mx-auto max-w-7xl px-4 md:px-6 py-8">
          <div className="relative overflow-hidden rounded-3xl">
            <img
              src={endingCover}
              alt="守护甜心结局夕阳与守护蛋插画"
              className="h-80 w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 to-transparent" />
            <p className="absolute inset-x-6 bottom-6 text-lg font-medium text-background-50 md:text-2xl">
              所有成长，最终都回归到了「理解与接纳」的原点。
            </p>
          </div>

          <article className="mx-auto mt-12 max-w-3xl">
            <h2 className="font-display text-2xl text-foreground-950 md:text-3xl">结局的一句话概括</h2>
            <p className="mt-4 text-base leading-8 text-foreground-800">
              守护甜心的结局并非「谁赢了谁输了」，而是所有角色都在经历守护蛋、X 蛋与自我认同的考验后，
              找到与自己和解、继续前行的方式。守护蛋与 X 蛋的纷争，最终回归到了对「真心」的理解与接纳——
              这既是整部作品的落点，也是它最动人的地方。
            </p>

            <h2 className="mt-12 font-display text-2xl text-foreground-950 md:text-3xl">主线结局时间线</h2>
            <ol className="mt-6 space-y-4">
              {timeline.map((t) => (
                <li key={t.no} className="flex gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-100 font-display text-lg text-primary-700">
                    {t.no}
                  </span>
                  <div>
                    <h3 className="font-medium text-foreground-950">{t.title}</h3>
                    <p className="mt-1 text-sm leading-6 text-foreground-700">{t.desc}</p>
                  </div>
                </li>
              ))}
            </ol>

            <h2 className="mt-12 font-display text-2xl text-foreground-950 md:text-3xl">所有角色的最终归宿</h2>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {endings.map((e) => (
                <div key={e.name} className="rounded-2xl border border-background-200/70 bg-background-50 p-5">
                  <div className="flex items-center gap-2">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-100 text-accent-800">
                      <i className="ri-user-heart-line" />
                    </span>
                    <div>
                      <h3 className="font-medium text-foreground-950">{e.name}</h3>
                      <p className="text-xs text-foreground-500">{e.role}</p>
                    </div>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-foreground-700">{e.desc}</p>
                </div>
              ))}
            </div>

            <h2 className="mt-12 font-display text-2xl text-foreground-950 md:text-3xl">开放结局，为什么反而更温柔？</h2>
            <p className="mt-4 text-base leading-8 text-foreground-700">
              守护甜心没有给恋爱线一个「标准答案」，却给了每个人物完整的成长弧光。这种开放式收尾不是逃避，
              而是把「接下来会怎样」的选择权温柔地交还给角色与观众：亚梦与几斗、与甜心们的关系，
              会在每个人的想象里继续延续下去。也正因如此，多年后重看，这部作品依然能带来新的感动。
            </p>

            <div className="mt-8 flex flex-col gap-3 rounded-2xl border border-background-200/70 bg-background-100 p-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-foreground-700">想回顾整部剧情脉络？按顺序看分集速览更清晰。</p>
              <Link
                to="/episodes"
                className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm text-background-50 whitespace-nowrap hover:bg-primary-600"
              >
                查看分集剧情 <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </article>

          <section className="mx-auto mt-14 max-w-5xl">
            <h2 className="text-center font-display text-2xl text-foreground-950">延伸阅读</h2>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
              {articles.slice(0, 3).map((a) => (
                <Link
                  key={a.id}
                  to={`/analysis/${a.id}`}
                  className="group overflow-hidden rounded-2xl border border-background-200/70 bg-background-50 transition-colors hover:border-primary-200"
                >
                  <div className="h-36 overflow-hidden">
                    <img
                      src={a.cover}
                      alt={a.title}
                      className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <span className="text-xs text-primary-600">{a.category}</span>
                    <h3 className="mt-1 font-medium text-sm leading-snug text-foreground-950 group-hover:text-primary-700">
                      {a.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}